import { Facebook, Linkedin, Instagram, Youtube, Twitter, Github, type LucideIcon } from "lucide-react";
import { useEffect, useState } from "react";
import logcomexAiLogo from "@/assets/logcomexai_logo.svg";
import logcomexAiLogoWhite from "@/assets/logcomexai_logo_white.svg";
import footerEndeavor from "@/assets/footer-endeavor.svg";
import footerGptw from "@/assets/footer-gptw.svg";
import { fetchSettings, readSettingsSync } from "@/lib/site-settings";

type SocialLink = { label: string; href: string; icon: string };
type FooterLink = { label: string; href: string; external?: boolean };
type LinkGroup = { title: string; links: FooterLink[] };
type FooterSettings = {
  social_links: SocialLink[];
  link_groups: LinkGroup[];
  legal_line: string;
  copyright: string;
};

const ICON_MAP: Record<string, LucideIcon> = {
  Facebook,
  Linkedin,
  Instagram,
  Youtube,
  Twitter,
  Github,
};

const FALLBACK: FooterSettings = {
  social_links: [
    { icon: "Facebook", href: "https://www.facebook.com/logcomex", label: "Facebook" },
    { icon: "Linkedin", href: "https://www.linkedin.com/company/logcomex/", label: "LinkedIn" },
    { icon: "Instagram", href: "https://www.instagram.com/logcomex/", label: "Instagram" },
    { icon: "Youtube", href: "https://www.youtube.com/c/Logcomex", label: "YouTube" },
  ],
  link_groups: [
    { title: "Logcomex.AI", links: [
      { label: "Missões", href: "https://logcomex.ai/#missoes", external: true },
      { label: "Planos", href: "https://logcomex.ai/#planos", external: true },
      { label: "FAQ", href: "https://logcomex.ai/#faq", external: true },
    ]},
    { title: "A Logcomex", links: [
      { label: "Sobre o blog", href: "/sobre" }, { label: "Sobre a Logcomex", href: "https://logcomex.ai/pt/sobre", external: true },
      { label: "Integrações", href: "https://logcomex.ai/pt/integracoes", external: true },
      { label: "Carreiras", href: "https://logcomex.ai/pt/carreiras", external: true },
      { label: "Imprensa", href: "https://logcomex.ai/pt/imprensa", external: true },
      { label: "Contato", href: "https://logcomex.ai/pt/contato", external: true },
    ]},
    { title: "Conteúdo", links: [
      { label: "Blog", href: "/" },
      { label: "Insights", href: "/insights" },
      { label: "Recursos", href: "/recursos" },
      { label: "Glossário", href: "/glossario" },
    ]},
    { title: "Legal", links: [
      { label: "Termos de uso", href: "https://trust.logcomex.ai/", external: true },
      { label: "Compliance", href: "https://logcomex.ai/pt/compliance", external: true },
      { label: "Privacidade", href: "https://logcomex.ai/pt/termos/privacidade", external: true },
    ]},
  ],
  legal_line: "Logcomex LTDA | CNPJ: 13.475.043/0001-75 | Av. Cândido de Abreu, 70 - Torre B - 1° andar - Centro Cívico, Curitiba - PR - Brasil +55 4003-3317",
  copyright: "Logcomex.AI · Todos os direitos reservados",
};

export default function Footer({ logoSrc }: { logoSrc?: string } = {}) {
  const logoToUse = logoSrc || logcomexAiLogo;
  const [settings, setSettings] = useState<FooterSettings>(() => ({
    ...FALLBACK,
    ...(readSettingsSync<Partial<FooterSettings>>("footer") ?? {}),
  }));

  useEffect(() => {
    let cancelled = false;
    fetchSettings<Partial<FooterSettings>>("footer").then((v) => {
      if (!cancelled && v) setSettings({ ...FALLBACK, ...v });
    });
    return () => { cancelled = true; };
  }, []);

  const { social_links: socialLinks, link_groups: linkGroups, legal_line, copyright } = settings;

  const [lang, setLang] = useState<string>(() => {
    if (typeof window === "undefined") return "pt";
    return localStorage.getItem("app_lang") || "pt";
  });
  useEffect(() => {
    const onLang = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (typeof detail === "string") setLang(detail);
    };
    window.addEventListener("app:lang-change", onLang);
    return () => window.removeEventListener("app:lang-change", onLang);
  }, []);

  const slugify = (s: string) =>
    s
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");

  const REWRITE_TITLES = new Set(["A Logcomex", "Legal"]);
  const resolveLink = (groupTitle: string, link: FooterLink) => {
    if (link.href && /^https?:\/\//i.test(link.href)) {
      return { href: link.href, external: true };
    }
    if (REWRITE_TITLES.has(groupTitle) && link.href !== "/sobre") {
      return {
        href: `https://logcomex.ai/${lang}/${slugify(link.label)}`,
        external: true,
      };
    }
    return { href: link.href, external: link.external };
  };

  return (
    <footer className="pb-10">
      <div className="max-w-[1280px] mx-auto px-8 max-md:px-5 flex flex-col gap-6">
        <section className="py-0 relative overflow-hidden">
          <svg className="absolute" width="0" height="0">
            <defs>
              <clipPath id="footer-clip" clipPathUnits="objectBoundingBox">
                <path d="
                  M 0.20,0.82
                  L 0.015,0.82
                  C 0.006,0.82 0,0.807 0,0.79
                  L 0,0.05
                  C 0,0.033 0.006,0.02 0.015,0.02
                  L 0.715,0.02
                  C 0.724,0.02 0.73,0.033 0.735,0.045
                  L 0.77,0.11
                  C 0.775,0.123 0.781,0.13 0.79,0.13
                  L 0.985,0.13
                  C 0.994,0.13 1,0.143 1,0.16
                  L 1,0.92
                  C 1,0.937 0.994,0.95 0.985,0.95
                  L 0.285,0.95
                  C 0.276,0.95 0.27,0.937 0.265,0.925
                  L 0.235,0.86
                  C 0.225,0.84 0.215,0.82 0.20,0.82
                  Z
                " />
              </clipPath>
            </defs>
          </svg>

          <div className="w-full relative z-10">
            <style>{`
              @media (min-width: 640px) {
                .footer-card { clip-path: url(#footer-clip); border-radius: 0 !important; }
              }
            `}</style>

            <div
              className="footer-card relative overflow-hidden px-8 py-10 sm:px-14 sm:py-20 lg:px-20 lg:py-24 text-center rounded-[12px] bg-[#DFE2F3] dark:bg-card dark:border dark:border-border"
            >
              <div className="relative z-10 flex flex-col sm:pt-0 lg:flex-row items-start gap-10 lg:gap-16">
                <div className="lg:w-[30%] flex flex-col items-center md:items-start md:flex-row md:justify-between lg:flex-col gap-4">
                  <div className="flex flex-col gap-4 items-start w-full">
                    <img src={logoToUse} alt="Logcomex.AI" width={396} height={61} className="w-4/5 h-auto block self-start dark:hidden" loading="lazy" decoding="async" />
                    <img src={logcomexAiLogoWhite} alt="Logcomex.AI" width={396} height={61} className="w-4/5 h-auto hidden dark:block self-start" loading="lazy" decoding="async" />
                    <div className="flex flex-col md:flex-row lg:flex-col items-center md:items-center gap-4 md:gap-8 mt-3">
                      <div className="flex items-center justify-center md:justify-start gap-8">
                        <img src={footerEndeavor} alt="Endeavor" width={120} height={24} className="h-6 dark:brightness-0 dark:invert" loading="lazy" decoding="async" />
                        <img src={footerGptw} alt="GPTW 2025" width={74} height={74} className="h-[74px]" loading="lazy" decoding="async" />
                      </div>
                      <div className="flex items-center gap-3 md:ml-auto lg:ml-0 lg:self-start">
                        {socialLinks.map((s) => {
                          const Icon = ICON_MAP[s.icon] ?? Linkedin;
                          return (
                            <a
                              key={s.label + s.href}
                              href={s.href}
                              target="_blank"
                              rel="noopener noreferrer"
                              aria-label={`Logcomex no ${s.label}`}
                              title={`Logcomex no ${s.label}`}
                              className="w-9 h-9 rounded-full border border-primary/30 flex items-center justify-center text-primary hover:text-primary/80 hover:border-primary transition-colors"
                            >
                              <Icon className="w-4 h-4" aria-hidden="true" />
                              <span className="sr-only">{`Logcomex no ${s.label}`}</span>
                            </a>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex-1 grid grid-cols-2 sm:grid-cols-4 gap-8 text-left pb-[10%] mt-[10%]">
                  {linkGroups.map((group) => (
                    <div key={group.title}>
                      <h4 className="text-sm font-semibold text-foreground mb-3">{group.title}</h4>
                      <ul className="flex flex-col gap-2">
                        {group.links.map((link) => {
                          const r = resolveLink(group.title, link);
                          return (
                            <li key={link.label + r.href}>
                              <a
                                href={r.href}
                                {...(r.external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                                className="text-sm text-muted-foreground hover:text-primary transition-colors"
                              >
                                {link.label}
                              </a>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>
              <p className="text-xs text-muted-foreground mt-6 text-center md:text-right w-full md:w-[70%] md:ml-auto">
                {legal_line}
              </p>
              <p className="text-xs text-muted-foreground mt-2 text-center md:text-right">
                © {new Date().getFullYear()} {copyright}
              </p>
            </div>
          </div>
        </section>
      </div>
    </footer>
  );
}
